import { useEffect, useState, useCallback, useMemo, Fragment } from "react";
import {
  Upload, FileSpreadsheet, Search, Trash2, Link2, Link2Off, GitCompare,
  Pencil, Plus, X, CheckCircle2, AlertTriangle, FileText, TrendingUp, Sparkles,
  ChevronDown, ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatDateShort } from "@/lib/format";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { AlbaranEditor } from "@/components/albaranes/AlbaranEditor";
import { LinkInvoiceDialog } from "@/components/albaranes/LinkInvoiceDialog";
import { AddLineToCatalogDialog } from "@/components/albaranes/AddLineToCatalogDialog";
import { CompareDialog } from "@/components/albaranes/CompareDialog";
import { DiscrepanciesModal } from "@/components/albaranes/DiscrepanciesModal";
import BulkUploadModal from "@/components/BulkUploadModal";

function fmt(value, currency = "EUR") {
  if (value == null) return "—";
  try {
    return new Intl.NumberFormat("es-ES", {
      style: "currency", currency, maximumFractionDigits: 2, useGrouping: "always",
    }).format(value);
  } catch { return `${value} ${currency}`; }
}

export default function Albaranes() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const baseCurrency = user?.base_currency || "EUR";
  const [albaranes, setAlbaranes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [q, setQ] = useState("");
  const [filterUnmatched, setFilterUnmatched] = useState(false);
  // Year filter (default = current year). "" = "Total histórico". The filter
  // is passed to /albaranes and /invoices as date_from / date_to so both
  // datasets stay in sync (needed for the linking suggestions).
  const [yearFilter, setYearFilter] = useState(String(new Date().getFullYear()));
  const [sortBy, setSortBy] = useState("date"); // date | vendor | albaran_number | total | line_items | invoice
  const [sortOrder, setSortOrder] = useState("desc");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [comparingInvId, setComparingInvId] = useState(null);
  const [linkingAlb, setLinkingAlb] = useState(null);
  // iter 82.7r — "Añadir línea a Productos" flow. Stores {albaranId, lineIndex, line}
  // while the confirmation dialog is open.
  const [addToCatalog, setAddToCatalog] = useState(null);
  const [discrepancies, setDiscrepancies] = useState(null);
  const [showDiscrepancies, setShowDiscrepancies] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  // Rows the user has expanded to see line items. Set of albaran ids.
  const [expandedIds, setExpandedIds] = useState(new Set());
  // iter 82.7u — multiselección para borrado en lote.
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const expandAll = () => setExpandedIds(new Set(albaranes.map((a) => a.id)));
  const collapseAll = () => setExpandedIds(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const albParams = {};
      const invParams = { type: "expense", limit: 500 };
      if (yearFilter) {
        albParams.date_from = `${yearFilter}-01-01`;
        albParams.date_to = `${yearFilter}-12-31`;
        invParams.start = `${yearFilter}-01-01`;
        invParams.end = `${yearFilter}-12-31`;
      }
      const [a, i, d] = await Promise.all([
        api.get("/albaranes", { params: albParams }),
        api.get("/invoices", { params: invParams }),
        api.get("/albaranes/discrepancies/monthly"),
      ]);
      setAlbaranes(a.data);
      setInvoices(i.data?.invoices || i.data || []);
      setDiscrepancies(d.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [yearFilter]);

  useEffect(() => { load(); }, [load]);

  const invoiceById = useMemo(() => {
    const m = {};
    for (const inv of invoices) m[inv.id] = inv;
    return m;
  }, [invoices]);

  const visible = useMemo(() => {
    let arr = albaranes.filter((a) => {
      if (q && !((a.vendor || "").toLowerCase().includes(q.toLowerCase()) ||
                 (a.albaran_number || "").toLowerCase().includes(q.toLowerCase()))) return false;
      if (filterUnmatched && a.invoice_id) return false;
      return true;
    });
    const keyMap = {
      date: (a) => a.date || "",
      created_at: (a) => a.created_at || "",
      vendor: (a) => (a.vendor || "").toLowerCase(),
      albaran_number: (a) => (a.albaran_number || "").toLowerCase(),
      total: (a) => Number(a.total) || 0,
      line_items: (a) => (a.line_items || []).length,
      invoice: (a) => a.invoice_id ? 1 : 0,
    };
    const k = keyMap[sortBy] || keyMap.date;
    arr = [...arr].sort((a, b) => {
      const av = k(a), bv = k(b);
      if (av < bv) return sortOrder === "desc" ? 1 : -1;
      if (av > bv) return sortOrder === "desc" ? -1 : 1;
      return 0;
    });
    return arr;
  }, [albaranes, q, filterUnmatched, sortBy, sortOrder]);

  const handleSortClick = (field) => {
    if (sortBy === field) setSortOrder(sortOrder === "desc" ? "asc" : "desc");
    else { setSortBy(field); setSortOrder(field === "vendor" || field === "albaran_number" ? "asc" : "desc"); }
  };
  const sortArrow = (field) => sortBy !== field ? "" : (sortOrder === "desc" ? " ▼" : " ▲");

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/albaranes/process-file", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const a = r.data.albaran;
      const suggs = r.data.suggestions || [];
      if (a.needs_review) {
        toast.warning(lang === "es"
          ? "Albarán subido — revisa los datos (OCR incompleto)"
          : "Uploaded — review needed (OCR incomplete)");
      } else if (suggs.length === 1) {
        // Single clear candidate → offer one-click link
        const inv = suggs[0];
        toast.success(
          lang === "es"
            ? `Albarán importado. Sugerencia: vincular a factura ${inv.invoice_number || inv.id.slice(-6)} (${inv.vendor})`
            : `Imported. Suggested invoice: ${inv.invoice_number || inv.id.slice(-6)} (${inv.vendor})`,
          {
            action: {
              label: lang === "es" ? "Vincular" : "Link",
              onClick: async () => {
                try {
                  await api.post(`/albaranes/${a.id}/link`, { invoice_id: inv.id });
                  toast.success(lang === "es" ? "Vinculado ✓" : "Linked ✓");
                  load();
                } catch (err) { toast.error(formatApiError(err)); }
              },
            },
            duration: 8000,
          }
        );
      } else if (suggs.length > 1) {
        toast.success(
          lang === "es"
            ? `Albarán importado. ${suggs.length} facturas candidatas — pulsa "Vincular".`
            : `Imported. ${suggs.length} candidate invoices — click "Link".`
        );
      } else {
        toast.success(lang === "es" ? "Albarán importado" : "Delivery note imported");
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const removeAlb = async (a) => {
    if (!window.confirm(lang === "es"
      ? `¿Borrar albarán de ${a.vendor || "?"} (${a.total} ${a.currency})?`
      : `Delete delivery note from ${a.vendor}?`)) return;
    try {
      await api.delete(`/albaranes/${a.id}`);
      toast.success(lang === "es" ? "Borrado" : "Deleted");
      setSelectedIds((prev) => { const n = new Set(prev); n.delete(a.id); return n; });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // iter 82.7u — multiselección helpers
  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const confirmMsg = lang === "es"
      ? `¿Borrar ${selectedIds.size} albarán(es) seleccionados? Esta acción no se puede deshacer.`
      : `Delete ${selectedIds.size} selected delivery note(s)? This cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;
    setBulkDeleting(true);
    try {
      const res = await api.post("/albaranes/bulk-delete", { ids: Array.from(selectedIds) });
      toast.success(lang === "es"
        ? `${res.data.deleted} albarán(es) borrado(s)`
        : `${res.data.deleted} delivery note(s) deleted`);
      setSelectedIds(new Set());
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBulkDeleting(false);
    }
  };

  const unlinkAlb = async (a) => {
    try {
      await api.post(`/albaranes/${a.id}/link`, { invoice_id: null });
      toast.success(lang === "es" ? "Desvinculado" : "Unlinked");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] text-[var(--text-muted)] mb-2">
            {lang === "es" ? "Aprovisionamiento" : "Procurement"}
          </p>
          <h1 className="font-display text-4xl font-bold tracking-tight leading-none flex items-center gap-2">
            <FileSpreadsheet size={32} />
            {lang === "es" ? "Albaranes" : "Delivery notes"}
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-2xl">
            {lang === "es"
              ? "Sube los albaranes (PDF/imagen). Se vinculan a la factura del proveedor para detectar diferencias de precio y cantidad."
              : "Upload delivery notes. Linked to invoices to detect price/quantity mismatches."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              data-testid="albaran-upload-input"
              onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ""; }}
              disabled={uploading}
            />
            <span
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium ${
                uploading ? "bg-[var(--surface-hover)] text-[var(--text-muted)]"
                          : "bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white cursor-pointer"
              }`}
            >
              <Upload size={14} />
              {uploading
                ? (lang === "es" ? "Procesando…" : "Processing…")
                : (lang === "es" ? "Subir albarán" : "Upload note")}
            </span>
          </label>
          <Button
            variant="outline"
            onClick={() => setBulkOpen(true)}
            data-testid="albaran-bulk-upload-btn"
          >
            <Sparkles size={14} className="mr-1.5 text-[var(--primary)]" />
            {lang === "es" ? "Subida masiva (IA)" : "Bulk upload (AI)"}
          </Button>
          <Button
            variant="outline"
            onClick={() => setEditing({
              vendor: "", date: "", albaran_number: "", total: "",
              currency: baseCurrency, line_items: [],
            })}
            data-testid="albaran-new-manual"
          >
            <Plus size={14} className="mr-1.5" />
            {lang === "es" ? "Manual" : "Manual"}
          </Button>
        </div>
      </div>

      <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={lang === "es" ? "Buscar proveedor o nº…" : "Search vendor or #…"}
            className="pl-9 h-9"
            data-testid="albaran-search"
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={filterUnmatched}
            onChange={(e) => setFilterUnmatched(e.target.checked)}
            data-testid="albaran-filter-unmatched"
            className="rounded"
          />
          {lang === "es" ? "Solo sin factura" : "Unlinked only"}
        </label>
        <div className="flex items-center gap-1">
          <span className="text-xs text-[var(--text-muted)]">
            {lang === "es" ? "Año:" : "Year:"}
          </span>
          <Select value={yearFilter || "total"} onValueChange={(v) => setYearFilter(v === "total" ? "" : v)}>
            <SelectTrigger className="h-9 w-40" data-testid="albaran-year-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(() => {
                const cy = new Date().getFullYear();
                return [cy, cy - 1, cy - 2, cy - 3, cy - 4].map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ));
              })()}
              <SelectItem value="total">{lang === "es" ? "Total histórico" : "Historic total"}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {discrepancies && discrepancies.issues_count > 0 && (
        <div
          className="bg-amber-50 dark:bg-amber-950/20 border border-amber-300 dark:border-amber-700 rounded-lg p-4 flex items-start gap-3"
          data-testid="discrepancies-tile"
        >
          <TrendingUp size={20} className="text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-medium text-amber-900 dark:text-amber-200">
              {lang === "es"
                ? `${discrepancies.issues_count} discrepancia${discrepancies.issues_count > 1 ? "s" : ""} este mes`
                : `${discrepancies.issues_count} mismatch${discrepancies.issues_count > 1 ? "es" : ""} this month`}
              {" · "}
              <span className="font-mono-num">
                {fmt(discrepancies.overpaid_total, baseCurrency)}
              </span>{" "}
              <span className="font-normal opacity-80">
                {lang === "es" ? "sobrepagados" : "overpaid"}
              </span>
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-300/80 mt-0.5">
              {lang === "es"
                ? `${discrepancies.impacted_invoices} factura${discrepancies.impacted_invoices > 1 ? "s" : ""} afectada${discrepancies.impacted_invoices > 1 ? "s" : ""} — el proveedor te cobró más caro que en el albarán.`
                : `${discrepancies.impacted_invoices} affected invoice${discrepancies.impacted_invoices > 1 ? "s" : ""} — vendor charged above the delivery note price.`}
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowDiscrepancies(true)}
            data-testid="open-discrepancies-modal"
          >
            {lang === "es" ? "Ver detalle" : "Details"}
          </Button>
        </div>
      )}

      {/* Bulk "add lines to Products" bar removed in iter 68 —
          albaranes are for price comparison only, never for expense creation. */}

      <div className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg overflow-x-auto">
        {selectedIds.size > 0 && (
          <div
            className="flex items-center justify-between gap-3 px-4 py-2 bg-[var(--primary)]/10 border-b border-[var(--primary)]/30"
            data-testid="albaran-bulk-bar"
          >
            <span className="text-sm font-medium text-[var(--primary)]">
              {lang === "es"
                ? `${selectedIds.size} albarán(es) seleccionado(s)`
                : `${selectedIds.size} delivery note(s) selected`}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setSelectedIds(new Set())}
                className="text-xs text-[var(--text-secondary)] hover:underline"
                data-testid="albaran-bulk-clear"
              >
                {lang === "es" ? "Cancelar" : "Clear"}
              </button>
              <button
                type="button"
                onClick={bulkDelete}
                disabled={bulkDeleting}
                className="inline-flex items-center gap-1 px-3 py-1 rounded bg-[var(--status-negative)] text-white text-xs font-medium hover:opacity-90 disabled:opacity-50"
                data-testid="albaran-bulk-delete"
              >
                <Trash2 size={13} />
                {bulkDeleting
                  ? (lang === "es" ? "Borrando…" : "Deleting…")
                  : (lang === "es" ? "Eliminar seleccionados" : "Delete selected")}
              </button>
            </div>
          </div>
        )}
        <table className="w-full text-sm" data-testid="albaranes-table">
          <thead className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] bg-[var(--surface-hover)]/40">
            <tr className="border-b border-[var(--border-color)]">
              <th className="w-10 px-2 text-center">
                <input
                  type="checkbox"
                  checked={visible.length > 0 && visible.every((a) => selectedIds.has(a.id))}
                  ref={(el) => { if (el) { el.indeterminate = visible.some((a) => selectedIds.has(a.id)) && !visible.every((a) => selectedIds.has(a.id)); } }}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedIds(new Set(visible.map((a) => a.id)));
                    } else {
                      setSelectedIds(new Set());
                    }
                  }}
                  className="rounded cursor-pointer"
                  data-testid="albaran-select-all"
                  aria-label={lang === "es" ? "Seleccionar todos" : "Select all"}
                />
              </th>
              <th className="w-8 px-1">
                <button
                  onClick={() => (expandedIds.size > 0 ? collapseAll() : expandAll())}
                  className="p-1 rounded hover:bg-[var(--surface-hover)] text-[var(--text-muted)]"
                  title={expandedIds.size > 0
                    ? (lang === "es" ? "Contraer todo" : "Collapse all")
                    : (lang === "es" ? "Expandir todo" : "Expand all")}
                  data-testid="albaran-toggle-all"
                >
                  {expandedIds.size > 0 ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
              </th>
              <th
                onClick={() => handleSortClick("date")}
                className="text-left py-2.5 px-3 cursor-pointer select-none hover:text-[var(--text-primary)]"
                data-testid="albaran-th-date"
              >{lang === "es" ? "Fecha" : "Date"}{sortArrow("date")}</th>
              <th
                onClick={() => handleSortClick("created_at")}
                className="text-left py-2.5 px-3 cursor-pointer select-none hover:text-[var(--text-primary)]"
                data-testid="albaran-th-created"
              >{lang === "es" ? "Subido" : "Uploaded"}{sortArrow("created_at")}</th>
              <th
                onClick={() => handleSortClick("vendor")}
                className="text-left py-2.5 px-3 cursor-pointer select-none hover:text-[var(--text-primary)]"
                data-testid="albaran-th-vendor"
              >{lang === "es" ? "Proveedor" : "Vendor"}{sortArrow("vendor")}</th>
              <th
                onClick={() => handleSortClick("albaran_number")}
                className="text-left py-2.5 px-3 cursor-pointer select-none hover:text-[var(--text-primary)]"
                data-testid="albaran-th-number"
              >{lang === "es" ? "Nº albarán" : "Note #"}{sortArrow("albaran_number")}</th>
              <th
                onClick={() => handleSortClick("total")}
                className="text-right py-2.5 px-3 cursor-pointer select-none hover:text-[var(--text-primary)]"
                data-testid="albaran-th-total"
              >Total{sortArrow("total")}</th>
              <th
                onClick={() => handleSortClick("line_items")}
                className="text-left py-2.5 px-3 cursor-pointer select-none hover:text-[var(--text-primary)]"
                data-testid="albaran-th-items"
              >{lang === "es" ? "Líneas" : "Items"}{sortArrow("line_items")}</th>
              <th
                onClick={() => handleSortClick("invoice")}
                className="text-left py-2.5 px-3 cursor-pointer select-none hover:text-[var(--text-primary)]"
                data-testid="albaran-th-invoice"
              >{lang === "es" ? "Factura" : "Invoice"}{sortArrow("invoice")}</th>
              <th className="w-32" />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={10} className="text-center py-10 text-[var(--text-muted)]">…</td></tr>
            )}
            {!loading && visible.length === 0 && (
              <tr><td colSpan={10} className="text-center py-16 text-[var(--text-muted)]">
                <FileSpreadsheet size={32} className="mx-auto mb-3 opacity-50" />
                {lang === "es"
                  ? "Aún no hay albaranes. Sube el primero con el botón \"Subir albarán\"."
                  : "No delivery notes yet. Upload one to get started."}
              </td></tr>
            )}
            {!loading && visible.map((a) => {
              const linked = a.invoice_id ? invoiceById[a.invoice_id] : null;
              const isExpanded = expandedIds.has(a.id);
              const lineCount = (a.line_items || []).length;
              return (
                <Fragment key={a.id}>
                <tr className={`border-b border-[var(--border-color)] last:border-0 hover:bg-[var(--surface-hover)] ${selectedIds.has(a.id) ? "bg-[var(--primary)]/5" : ""}`}
                    data-testid={`albaran-row-${a.id}`}>
                  <td className="py-2.5 px-2 w-10 text-center">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(a.id)}
                      onChange={() => toggleSelected(a.id)}
                      className="rounded cursor-pointer"
                      data-testid={`albaran-select-${a.id}`}
                      aria-label={lang === "es" ? `Seleccionar albarán ${a.albaran_number || a.id}` : `Select delivery note ${a.albaran_number || a.id}`}
                    />
                  </td>
                  <td className="py-2.5 px-1 w-8">
                    {lineCount > 0 && (
                      <button
                        onClick={() => toggleExpanded(a.id)}
                        className="p-1 rounded hover:bg-[var(--surface-hover)] text-[var(--text-muted)]"
                        title={isExpanded ? (lang === "es" ? "Contraer" : "Collapse") : (lang === "es" ? "Ver líneas" : "Show items")}
                        data-testid={`albaran-toggle-${a.id}`}
                        aria-expanded={isExpanded}
                      >
                        {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>
                    )}
                  </td>
                  <td className="py-2.5 px-3 font-mono-num text-xs">{a.date || "—"}</td>
                  <td className="py-2.5 px-3 font-mono-num text-xs text-[var(--text-muted)]">
                    {a.created_at ? formatDateShort(a.created_at) : "—"}
                  </td>
                  <td className="py-2.5 px-3">
                    {a.vendor || <span className="text-[var(--text-muted)] italic">?</span>}
                    {a.needs_review && (
                      <AlertTriangle size={12} className="inline ml-1.5 text-amber-500" />
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-xs font-mono-num">{a.albaran_number || "—"}</td>
                  <td className="py-2.5 px-3 text-right font-mono-num">{fmt(a.total, a.currency)}</td>
                  <td className="py-2.5 px-3 text-xs text-[var(--text-muted)]">
                    {(a.line_items || []).length}
                  </td>
                  <td className="py-2.5 px-3 text-xs">
                    {linked ? (
                      <span className="inline-flex items-center gap-1 text-[var(--status-positive)]">
                        <CheckCircle2 size={12} />
                        {linked.invoice_number || linked.id.slice(-6)}
                      </span>
                    ) : (
                      <button
                        onClick={() => setLinkingAlb(a)}
                        className="text-[var(--primary)] hover:underline"
                        data-testid={`albaran-link-${a.id}`}
                      >
                        {lang === "es" ? "Vincular…" : "Link…"}
                      </button>
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-right">
                    {linked && (
                      <>
                        <button
                          onClick={() => setComparingInvId(linked.id)}
                          className="p-1.5 rounded hover:bg-[var(--surface-hover)] text-[var(--text-secondary)]"
                          title={lang === "es" ? "Comparar con factura" : "Compare"}
                          data-testid={`albaran-compare-${a.id}`}
                        >
                          <GitCompare size={14} />
                        </button>
                        <button
                          onClick={() => unlinkAlb(a)}
                          className="p-1.5 rounded hover:bg-[var(--surface-hover)] text-[var(--text-secondary)]"
                          title={lang === "es" ? "Desvincular" : "Unlink"}
                          data-testid={`albaran-unlink-${a.id}`}
                        >
                          <Link2Off size={14} />
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => setEditing({
                        ...a,
                        total: String(a.total ?? ""),
                        line_items: (a.line_items || []).map((li) => ({ ...li })),
                      })}
                      className="p-1.5 rounded hover:bg-[var(--surface-hover)] text-[var(--text-secondary)]"
                      title={lang === "es" ? "Editar" : "Edit"}
                      data-testid={`albaran-edit-${a.id}`}
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => removeAlb(a)}
                      className="p-1.5 rounded hover:bg-[var(--surface-hover)] text-[var(--status-negative)]"
                      title={lang === "es" ? "Borrar" : "Delete"}
                      data-testid={`albaran-delete-${a.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
                {isExpanded && lineCount > 0 && (
                  <tr className="border-b border-[var(--border-color)] bg-[var(--surface-hover)]/30" data-testid={`albaran-expanded-${a.id}`}>
                    <td colSpan={10} className="px-6 py-3">
                      <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-2">
                        {lang === "es" ? `Líneas del albarán · ${lineCount}` : `Line items · ${lineCount}`}
                      </div>
                      <table className="w-full text-xs">
                        <thead className="text-[10px] uppercase text-[var(--text-muted)]">
                          <tr>
                            <th className="text-left py-1.5 px-2 font-medium">{lang === "es" ? "Producto" : "Product"}</th>
                            <th className="text-right py-1.5 px-2 font-medium w-16">{lang === "es" ? "Cant." : "Qty"}</th>
                            <th className="text-left py-1.5 px-2 font-medium w-16">Ud</th>
                            <th className="text-right py-1.5 px-2 font-medium w-24">{lang === "es" ? "Precio" : "Price"}</th>
                            <th className="text-right py-1.5 px-2 font-medium w-24">Total</th>
                            <th className="text-right py-1.5 px-2 font-medium w-32"> </th>
                          </tr>
                        </thead>
                        <tbody>
                          {(a.line_items || []).map((li, i) => {
                            return (
                            <tr key={`${a.id}-li-${i}`} className="border-t border-[var(--border-color)]/50">
                              <td className="py-1.5 px-2">{li.name || "—"}</td>
                              <td className="py-1.5 px-2 text-right font-mono-num">{li.qty ?? "—"}</td>
                              <td className="py-1.5 px-2 text-[var(--text-muted)]">{li.unit || ""}</td>
                              <td className="py-1.5 px-2 text-right font-mono-num">
                                {li.unit_price != null ? fmt(li.unit_price, a.currency) : "—"}
                              </td>
                              <td className="py-1.5 px-2 text-right font-mono-num">
                                {li.total != null ? fmt(li.total, a.currency) : "—"}
                              </td>
                              <td className="py-1.5 px-2 text-right">
                                {li.name && (
                                  <button
                                    type="button"
                                    onClick={() => setAddToCatalog({ albaranId: a.id, lineIndex: i, line: li })}
                                    className="text-[10px] text-[var(--brand-accent)] hover:underline"
                                    data-testid={`albaran-line-add-to-catalog-${a.id}-${i}`}
                                    title={lang === "es"
                                      ? "Añadir esta línea al catálogo de Productos"
                                      : "Add this line to Products catalog"}
                                  >
                                    + {lang === "es" ? "Producto" : "Product"}
                                  </button>
                                )}
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <AlbaranEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={load}
          baseCurrency={baseCurrency}
          lang={lang}
        />
      )}

      {addToCatalog && (
        <AddLineToCatalogDialog
          payload={addToCatalog}
          onClose={() => setAddToCatalog(null)}
          baseCurrency={baseCurrency}
          lang={lang}
        />
      )}

      {linkingAlb && (
        <LinkInvoiceDialog
          albaran={linkingAlb}
          invoices={invoices}
          onClose={() => setLinkingAlb(null)}
          onLinked={load}
          lang={lang}
        />
      )}

      {comparingInvId && (
        <CompareDialog
          invoiceId={comparingInvId}
          onClose={() => setComparingInvId(null)}
          onChanged={load}
          baseCurrency={baseCurrency}
          lang={lang}
        />
      )}

      {showDiscrepancies && discrepancies && (
        <DiscrepanciesModal
          data={discrepancies}
          baseCurrency={baseCurrency}
          onClose={() => setShowDiscrepancies(false)}
          onOpenInvoice={(invId) => { setShowDiscrepancies(false); setComparingInvId(invId); }}
          lang={lang}
        />
      )}

      <BulkUploadModal
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        onDone={load}
        mode="albaranes"
      />
    </div>

  );
}
